/* src/world/contextLayer.ts — the whole country, always resident.
 *
 * One static mesh set for coastline, urban patches, state borders and major highways across
 * Australia, built once by the context worker and parented under the tile rings. It exists
 * so the view past the surveyed cells reads as real countryside — the coast, the Hume, the
 * next town over — instead of a green void.
 *
 * Placement uses the same trick as tiles: geometry is built in mercator metres scaled at the
 * origin captured at build time, and the group corrects position and scale against the
 * *current* origin every rebase. Distortion across a continent-sized mesh is a few percent at
 * the edges — context, not survey data, and invisible at the distances it is seen from.
 */

import { Group, type Object3D } from 'three';
import { mercToLocal, type Origin } from '../map/originRebase.ts';
import { createTileObject, type TileObject } from '../render/tileMesh.ts';
import type { LayerMaterials } from '../render/materials.ts';
import type { ContextWorkerIn, ContextWorkerOut, LabelPoint } from '../workers/protocol.ts';

/** Group base height: below the far ring (−0.35) with steps wide enough for far-field depth
 * precision (~1.4 m at the fog edge). Layer lifts inside the worker are 0…4.2 on top of this,
 * so the context spans −6.0…−1.8 — metres below the tile rings, invisible from any distance
 * it is actually seen at. */
const CONTEXT_Y = -6.0;

export interface ContextLayer {
  readonly root: Object3D;
  /** Town/city label points, once loaded — the flat view's name layer reads these. */
  readonly places: readonly LabelPoint[];
  /** Ask the shared worker for the globe texture; resolves with labels alongside. */
  bakeGlobeTexture: (width: number, height: number) => Promise<{
    bitmap: ImageBitmap;
    countries: readonly LabelPoint[];
    states: readonly LabelPoint[];
  }>;
  setOrigin: (origin: Origin) => void;
  dispose: () => void;
}

export function createContextLayer(
  scene: Object3D,
  materials: LayerMaterials,
  origin: Origin,
): ContextLayer {
  const root = new Group();
  root.matrixAutoUpdate = false;
  scene.add(root);

  let current: Origin = origin;
  let built: { object: TileObject; anchorMercX: number; anchorMercY: number; scale: number } | null =
    null;
  let places: readonly LabelPoint[] = [];
  let disposed = false;
  let nextId = 0;

  const pendingTexture = new Map<string, {
    resolve: (v: { bitmap: ImageBitmap; countries: readonly LabelPoint[]; states: readonly LabelPoint[] }) => void;
    reject: (e: Error) => void;
  }>();

  const place = (): void => {
    if (!built) return;
    const [x, z] = mercToLocal(built.anchorMercX, built.anchorMercY, current);
    const r = current.scale / built.scale;
    built.object.group.position.set(x, CONTEXT_Y, z);
    built.object.group.scale.set(r, 1, r);
    built.object.group.updateMatrix();
  };

  /*
   * The worker retires whenever it has nothing pending: its heap holds the parsed country
   * packs (tens of megabytes V8 will not hand back), and its only recurring job — re-baking
   * the globe texture — happens behind a fade that hides a fresh worker's warm-up entirely.
   */
  let worker: Worker | null = null;
  const retireIfIdle = (): void => {
    if (worker && pendingTexture.size === 0) {
      worker.terminate();
      worker = null;
    }
  };
  const ensureWorker = (): Worker => {
    if (worker) return worker;
    worker = new Worker(new URL('../workers/contextWorker.ts', import.meta.url), {
      type: 'module',
    });
    worker.onmessage = onMessage;
    return worker;
  };

  const onMessage = (e: MessageEvent<ContextWorkerOut>): void => {
    const msg = e.data;
    if (msg.kind === 'context-built') {
      if (disposed) return;
      // Context is never clicked; its JS-side arrays free themselves after GPU upload.
      // Its depthless ground is the bottom of the painter's stack (-200 under both rings).
      const object = createTileObject(msg.meshes, materials, false, -200);
      // Context is scenery: it neither casts nor receives the street-level shadow pass.
      for (const child of object.group.children) {
        child.castShadow = false;
        child.receiveShadow = false;
      }
      // Anchor to the origin the worker was GIVEN, not the origin at arrival — they differ
      // if the player travelled while the worker was busy, and place() corrects from there.
      built = {
        object,
        anchorMercX: requestOrigin.mercX,
        anchorMercY: requestOrigin.mercY,
        scale: requestOrigin.scale,
      };
      places = msg.places;
      place();
      root.add(object.group);
      retireIfIdle();
      return;
    }
    if (msg.kind === 'globe-texture-done') {
      pendingTexture.get(msg.id)?.resolve({
        bitmap: msg.bitmap,
        countries: msg.countries,
        states: msg.states,
      });
      pendingTexture.delete(msg.id);
      retireIfIdle();
      return;
    }
    // CLAUDE.md: world data may fail, the layer degrades, the map keeps rendering.
    console.warn('[context]', msg.message);
    pendingTexture.get(msg.id)?.reject(new Error(msg.message));
    pendingTexture.delete(msg.id);
    retireIfIdle();
  };

  // Kick the build immediately; the packs fetch lazily inside the worker.
  const requestOrigin = current;
  const req: ContextWorkerIn = {
    kind: 'context-build',
    id: `c${String(nextId++)}`,
    originMercX: requestOrigin.mercX,
    originMercY: requestOrigin.mercY,
    metreScale: requestOrigin.scale,
  };
  ensureWorker().postMessage(req);

  return {
    root,
    get places(): readonly LabelPoint[] {
      return places;
    },

    bakeGlobeTexture(width: number, height: number) {
      return new Promise((resolve, reject) => {
        const id = `g${String(nextId++)}`;
        pendingTexture.set(id, { resolve, reject });
        const msg: ContextWorkerIn = { kind: 'globe-texture', id, width, height };
        ensureWorker().postMessage(msg);
      });
    },

    setOrigin(next: Origin): void {
      current = next;
      place();
    },

    dispose(): void {
      disposed = true;
      worker?.terminate();
      worker = null;
      if (built) {
        root.remove(built.object.group);
        built.object.dispose();
        built = null;
      }
      scene.remove(root);
    },
  };
}
